import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getAllQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { format, toDate } from "date-fns-tz";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  TRANSFER_TYPE_ALLOWED_EXTRA_FIELDS_CODES,
  TransferDetailsTypeReasonsCodes,
  TransferOfCareTransferDetailsQuestionsLinkIds,
} from "@/encounters/constants/transfer-of-care";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { booleanToYesOrNo } from "@/utils/medplum";
import { getDisplayTimeValue } from "@/utils/timeUtils";

const TransferDetailsDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.TransferOfCareTransferDetails,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return null;

  const previousTransferDetailsAnswers = getAllQuestionnaireAnswers(previousVersion);
  const currentTransferDetailsAnswers = getAllQuestionnaireAnswers(currentVersion);

  const previousTransferDetailsDate =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.date]?.[0]
      ?.valueDateTime;
  const currentTransferDetailsDate =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.date]?.[0]
      ?.valueDateTime;

  const previousTransferDetailsTime =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.time]?.[0]
      ?.valueString;
  const currentTransferDetailsTime =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.time]?.[0]
      ?.valueString;

  const previousTransferDetailsTransferType =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.transferType]?.[0]
      ?.valueCoding;
  const currentTransferDetailsTransferType =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.transferType]?.[0]
      ?.valueCoding;

  const previousTransferDetailsNotes =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.notes]?.[0]
      ?.valueString;
  const currentTransferDetailsNotes =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.notes]?.[0]
      ?.valueString;

  const previousTransferDetailsReasons = previousTransferDetailsAnswers[
    TransferOfCareTransferDetailsQuestionsLinkIds.reasons
  ]?.map((value) => JSON.parse(value.valueString ?? "")?.display ?? "");

  const currentTransferDetailsReasons = currentTransferDetailsAnswers[
    TransferOfCareTransferDetailsQuestionsLinkIds.reasons
  ]?.map((value) => JSON.parse(value.valueString ?? "")?.display ?? "");

  const previousTransferDetailsUrgency =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.urgency]?.[0]
      ?.valueCoding;
  const currentTransferDetailsUrgency =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.urgency]?.[0]
      ?.valueCoding;

  const previousTransferDetailsCondition =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.condition]?.[0]
      ?.valueCoding;
  const currentTransferDetailsCondition =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.condition]?.[0]
      ?.valueCoding;

  const previousTransferDetailsTransferMode =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.transferMode]?.[0]
      ?.valueCoding;
  const currentTransferDetailsTransferMode =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.transferMode]?.[0]
      ?.valueCoding;

  const previousTransferDetailsMidwifeStayWithClient =
    previousTransferDetailsAnswers[
      TransferOfCareTransferDetailsQuestionsLinkIds.midwifeStayWithClient
    ]?.[0]?.valueBoolean;
  const currentTransferDetailsMidwifeStayWithClient =
    currentTransferDetailsAnswers[
      TransferOfCareTransferDetailsQuestionsLinkIds.midwifeStayWithClient
    ]?.[0]?.valueBoolean;

  const previousTransferDetailsDecisionDate =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.decisionDate]?.[0]
      ?.valueDateTime;
  const currentTransferDetailsDecisionDate =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.decisionDate]?.[0]
      ?.valueDateTime;

  const previousTransferDetailsDecisionTime =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.decisionTime]?.[0]
      ?.valueString;
  const currentTransferDetailsDecisionTime =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.decisionTime]?.[0]
      ?.valueString;

  const previousTransferDetailsDepartureDate =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.departureDate]?.[0]
      ?.valueDateTime;
  const currentTransferDetailsDepartureDate =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.departureDate]?.[0]
      ?.valueDateTime;

  const previousTransferDetailsDepartureTime =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.departureTime]?.[0]
      ?.valueString;
  const currentTransferDetailsDepartureTime =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.departureTime]?.[0]
      ?.valueString;

  const previousTransferDetailsArrivalDate =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.arrivalDate]?.[0]
      ?.valueDateTime;
  const currentTransferDetailsArrivalDate =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.arrivalDate]?.[0]
      ?.valueDateTime;

  const previousTransferDetailsArrivalTime =
    previousTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.arrivalTime]?.[0]
      ?.valueString;
  const currentTransferDetailsArrivalTime =
    currentTransferDetailsAnswers[TransferOfCareTransferDetailsQuestionsLinkIds.arrivalTime]?.[0]
      ?.valueString;

  const hasExtraFields = TRANSFER_TYPE_ALLOWED_EXTRA_FIELDS_CODES.some(
    (code) =>
      code === (previousTransferDetailsTransferType?.code as TransferDetailsTypeReasonsCodes) ||
      code === (currentTransferDetailsTransferType?.code as TransferDetailsTypeReasonsCodes),
  );

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Transfer Details</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Date</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={
                    currentTransferDetailsDate
                      ? format(toDate(currentTransferDetailsDate), "MM/dd/yyyy")
                      : ""
                  }
                  previous={
                    previousTransferDetailsDate
                      ? format(toDate(previousTransferDetailsDate), "MM/dd/yyyy")
                      : " "
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={getDisplayTimeValue(currentTransferDetailsTime)}
                  previous={getDisplayTimeValue(previousTransferDetailsTime)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Transfer Type</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferDetailsTransferType?.display}
                  previous={previousTransferDetailsTransferType?.display}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Reasons</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferDetailsReasons?.join(" - ")}
                  previous={previousTransferDetailsReasons?.join(" - ")}
                />
              </Stack>
              {hasExtraFields && (
                <>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Urgency</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={currentTransferDetailsUrgency?.display}
                      previous={previousTransferDetailsUrgency?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Condition</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={currentTransferDetailsCondition?.display}
                      previous={previousTransferDetailsCondition?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Transfer Mode</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={currentTransferDetailsTransferMode?.display}
                      previous={previousTransferDetailsTransferMode?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Midwife Stay with Client</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={booleanToYesOrNo(currentTransferDetailsMidwifeStayWithClient)}
                      previous={booleanToYesOrNo(previousTransferDetailsMidwifeStayWithClient)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Decision Date</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={
                        currentTransferDetailsDecisionDate
                          ? format(toDate(currentTransferDetailsDecisionDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsDecisionDate
                          ? format(toDate(previousTransferDetailsDecisionDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Decision Time</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={getDisplayTimeValue(currentTransferDetailsDecisionTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsDecisionTime)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Departure Date</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={
                        currentTransferDetailsDepartureDate
                          ? format(toDate(currentTransferDetailsDepartureDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsDepartureDate
                          ? format(toDate(previousTransferDetailsDepartureDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Departure Time</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={getDisplayTimeValue(currentTransferDetailsDepartureTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsDepartureTime)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Arrival Date</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={
                        currentTransferDetailsArrivalDate
                          ? format(toDate(currentTransferDetailsArrivalDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsArrivalDate
                          ? format(toDate(previousTransferDetailsArrivalDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Arrival Time</TextWrapper>
                    <WordsDiff
                      showRemoved
                      current={getDisplayTimeValue(currentTransferDetailsArrivalTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsArrivalTime)}
                    />
                  </Stack>
                </>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTransferDetailsNotes}
                  previous={previousTransferDetailsNotes}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Date</TextWrapper>
                <WordsDiff
                  current={
                    currentTransferDetailsDate
                      ? format(toDate(currentTransferDetailsDate), "MM/dd/yyyy")
                      : ""
                  }
                  previous={
                    previousTransferDetailsDate
                      ? format(toDate(previousTransferDetailsDate), "MM/dd/yyyy")
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  current={getDisplayTimeValue(currentTransferDetailsTime)}
                  previous={getDisplayTimeValue(previousTransferDetailsTime)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Transfer Type</TextWrapper>
                <WordsDiff
                  current={currentTransferDetailsTransferType?.display}
                  previous={previousTransferDetailsTransferType?.display}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Reasons</TextWrapper>
                <WordsDiff
                  current={currentTransferDetailsReasons?.join(" - ")}
                  previous={previousTransferDetailsReasons?.join(" - ")}
                />
              </Stack>
              {hasExtraFields && (
                <>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Urgency</TextWrapper>
                    <WordsDiff
                      current={currentTransferDetailsUrgency?.display}
                      previous={previousTransferDetailsUrgency?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Condition</TextWrapper>
                    <WordsDiff
                      current={currentTransferDetailsCondition?.display}
                      previous={previousTransferDetailsCondition?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Transfer Mode</TextWrapper>
                    <WordsDiff
                      current={currentTransferDetailsTransferMode?.display}
                      previous={previousTransferDetailsTransferMode?.display}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Midwife Stay with Client</TextWrapper>
                    <WordsDiff
                      current={booleanToYesOrNo(currentTransferDetailsMidwifeStayWithClient)}
                      previous={booleanToYesOrNo(previousTransferDetailsMidwifeStayWithClient)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Decision Date</TextWrapper>
                    <WordsDiff
                      current={
                        currentTransferDetailsDecisionDate
                          ? format(toDate(currentTransferDetailsDecisionDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsDecisionDate
                          ? format(toDate(previousTransferDetailsDecisionDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Decision Time</TextWrapper>
                    <WordsDiff
                      current={getDisplayTimeValue(currentTransferDetailsDecisionTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsDecisionTime)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Departure Date</TextWrapper>
                    <WordsDiff
                      current={
                        currentTransferDetailsDepartureDate
                          ? format(toDate(currentTransferDetailsDepartureDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsDepartureDate
                          ? format(toDate(previousTransferDetailsDepartureDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Departure Time</TextWrapper>
                    <WordsDiff
                      current={getDisplayTimeValue(currentTransferDetailsDepartureTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsDepartureTime)}
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Arrival Date</TextWrapper>
                    <WordsDiff
                      current={
                        currentTransferDetailsArrivalDate
                          ? format(toDate(currentTransferDetailsArrivalDate), "MM/dd/yyyy")
                          : ""
                      }
                      previous={
                        previousTransferDetailsArrivalDate
                          ? format(toDate(previousTransferDetailsArrivalDate), "MM/dd/yyyy")
                          : ""
                      }
                    />
                  </Stack>
                  <Stack gap="0">
                    <TextWrapper fw={600}>Arrival Time</TextWrapper>
                    <WordsDiff
                      current={getDisplayTimeValue(currentTransferDetailsArrivalTime)}
                      previous={getDisplayTimeValue(previousTransferDetailsArrivalTime)}
                    />
                  </Stack>
                </>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff
                  current={currentTransferDetailsNotes}
                  previous={previousTransferDetailsNotes}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default TransferDetailsDiff;
